import { CartAddEvent } from '@theme/events';

/**
 * Cart Delivery Validation
 * Checks if cart contains paving products without delivery items and blocks checkout
 */

class CartDeliveryValidation {
  constructor() {
    this.init();
  }

  async init() {
    // Check on page load
    await this.validateCart();
    
    // Listen for cart updates
    document.addEventListener('cart:update', () => this.validateCart());
    document.addEventListener('cart:change', () => this.validateCart());
    document.addEventListener('cart:refresh', () => this.validateCart());
    document.addEventListener(CartAddEvent.eventName, () => this.validateCart());
  }

  async validateCart() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return;
      
      const cart = await response.json();
      if (!cart || !cart.items || cart.items.length === 0) {
        this.clearValidation();
        return;
      }
      
      // Identify paving products (from calculator or single pallet flow)
      // Paving products have delivery zone properties but are NOT delivery products themselves
      const pavingProducts = cart.items.filter((/** @type {any} */ item) => {
        if (!item.properties) return false;
        
        // Check if item has delivery zone properties (indicates it's a paving product)
        const hasDeliveryZone = item.properties['Delivery Zone'] || item.properties['_delivery_zone'];
        if (!hasDeliveryZone) return false;
        
        // Exclude delivery products by checking product handle/title
        // Delivery products have handle like "delivery-paving-slabs" or title includes "delivery"
        const productHandle = (item.product_handle || '').toLowerCase();
        const productTitle = (item.product_title || '').toLowerCase();
        const isDeliveryProduct = productHandle.includes('delivery-paving-slabs') || 
                                  productHandle.includes('delivery') ||
                                  productTitle.includes('delivery') && !productTitle.includes('paving');
        
        // Paving product: has delivery zone AND is not a delivery product
        return !isDeliveryProduct;
      });
      
      if (pavingProducts.length === 0) {
        this.clearValidation();
        return;
      }
      
      // Check if cart has delivery product
      // Delivery products are identified by:
      // 1. Product handle/title includes "delivery-paving-slabs" or "delivery"
      // 2. Has 'Pack Count' or '_calculated_delivery_cost_pence' properties (unique to delivery products)
      const hasDeliveryProduct = cart.items.some((/** @type {any} */ item) => {
        if (!item.properties) return false;
        
        // Check by properties unique to delivery products
        const hasDeliveryProductProps = item.properties['Pack Count'] || 
                                        item.properties['_calculated_delivery_cost_pence'] ||
                                        item.properties['_delivery_quantity'];
        
        if (hasDeliveryProductProps) return true;
        
        // Check by product handle/title (fallback)
        const productHandle = (item.product_handle || '').toLowerCase();
        const productTitle = (item.product_title || '').toLowerCase();
        const variantTitle = (item.variant_title || '').toLowerCase();
        
        // Check by product handle (most reliable)
        const isDeliveryByHandle = productHandle.includes('delivery-paving-slabs') || 
                                    productHandle.includes('delivery');
        
        // Check by title (fallback - be careful not to match calculator products)
        const isDeliveryByTitle = (productTitle.includes('delivery') && 
                                   !productTitle.includes('paving')) || // "Delivery" but not "Paving Delivery"
                                   (productTitle.includes('delivery') && variantTitle.includes('zone'));
        
        return isDeliveryByHandle || isDeliveryByTitle;
      });
      
      // If paving products exist but no delivery product, show warning
      if (!hasDeliveryProduct) {
        this.showValidationWarning();
      } else {
        this.clearValidation();
      }
    } catch (error) {
      console.error('Cart validation failed', error);
    }
  }

  showValidationWarning() {
    // Disable checkout buttons
    const checkoutButtons = document.querySelectorAll(
      'a[href*="/checkout"], button[data-checkout], .cart__checkout, [data-cart-checkout], #checkout, button[name="checkout"]'
    );
    
    checkoutButtons.forEach(button => {
      if (button instanceof HTMLAnchorElement) {
        button.style.pointerEvents = 'none';
        button.style.opacity = '0.5';
        button.style.cursor = 'not-allowed';
        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('data-delivery-required', 'true');
        button.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }, true);
      } else if (button instanceof HTMLButtonElement) {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('data-delivery-required', 'true');
      }
    });
    
    // Show warning message
    let warningEl = document.querySelector('.cart__delivery-warning');
    if (!warningEl) {
      const warningDiv = document.createElement('div');
      warningDiv.className = 'cart__delivery-warning';
      warningDiv.style.cssText = 'padding: 1rem; margin: 1rem 0; background-color: #fff3cd; border: 1px solid #ffc107; border-radius: 4px; color: #856404;';
      
      const cartSummary = document.querySelector('.cart-drawer__summary, .cart__summary, [data-cart-summary]');
      const checkoutBtn = document.querySelector('a[href*="/checkout"], button[data-checkout], .cart__checkout');
      
      if (checkoutBtn && checkoutBtn.parentElement) {
        checkoutBtn.parentElement.insertBefore(warningDiv, checkoutBtn);
      } else if (cartSummary && cartSummary instanceof HTMLElement) {
        cartSummary.insertBefore(warningDiv, cartSummary.firstChild);
      }
      
      warningEl = warningDiv;
    }
    
    if (warningEl instanceof HTMLElement) {
      warningEl.innerHTML = `
      <div style="display: flex; align-items: flex-start; gap: 0.75rem;">
        <span style="font-size: 1.25rem;">⚠️</span>
        <div style="flex: 1;">
          <strong>Delivery details are required for pallet products.</strong>
          <p style="margin: 0.5rem 0 0 0; font-size: 0.875rem;">
            Please calculate delivery before checkout.
          </p>
          <button 
            type="button" 
            class="button button--secondary" 
            style="margin-top: 0.75rem;"
            onclick="window.location.href='${window.location.pathname}'"
          >
            Calculate delivery
          </button>
        </div>
      </div>
    `;
      warningEl.style.display = 'block';
    }
  }

  clearValidation() {
    // Re-enable checkout buttons
    const checkoutButtons = document.querySelectorAll('[data-delivery-required="true"]');
    checkoutButtons.forEach(button => {
      if (button instanceof HTMLAnchorElement) {
        button.style.pointerEvents = '';
        button.style.opacity = '';
        button.style.cursor = '';
        button.removeAttribute('aria-disabled');
        button.removeAttribute('data-delivery-required');
      } else if (button instanceof HTMLButtonElement) {
        button.disabled = false;
        button.removeAttribute('aria-disabled');
        button.removeAttribute('data-delivery-required');
      }
    });
    
    // Hide warning
    const warningEl = document.querySelector('.cart__delivery-warning');
    if (warningEl && warningEl instanceof HTMLElement) {
      warningEl.style.display = 'none';
    }
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CartDeliveryValidation();
  });
} else {
  new CartDeliveryValidation();
}

